import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bot, Check, ChevronsUpDown, ExternalLink, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Label } from '../../../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { useUsers } from '@/shared/users/hooks/useUsers';
import type { User, UserType } from '@/shared/users/types';
import { API_BASE_URL } from '../../api/config';

function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  return (first + last).toUpperCase() || '?';
}

function getAvatarUrl(user: User): string | undefined {
  if (!user.profile_image_url) return undefined;
  return `${API_BASE_URL}${user.profile_image_url}`;
}

interface UserPickerProps {
  value: string | null;
  onChange: (userId: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  showUserType?: boolean;
  filterUserTypes?: UserType[];
  /** ALLOW-list: when set, ONLY these users are offered. */
  filterUserIds?: string[];
  /**
   * DENY-list: these users are removed from the options.
   *
   * The counterpart to `filterUserIds`, for the pairing case — "anyone except
   * me", "anyone not already on this team". Passing the excluded ids to
   * `filterUserIds` does the exact opposite (it offers ONLY them), which is a
   * silent inversion the type system can't catch, so the two are separate props
   * rather than one prop with a mode flag.
   */
  excludeUserIds?: string[];
  /** Hide email in trigger button (show only name). Email still visible in dropdown list. */
  compact?: boolean;
}

export function UserPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  showUserType = true,
  filterUserTypes,
  filterUserIds,
  excludeUserIds,
  compact = false,
}: UserPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t('pickers.selectUser', 'Select user');

  const { data: usersData, isLoading } = useUsers();

  const users = useMemo(() => {
    if (!usersData?.data) return [];
    let filtered = usersData.data;
    if (filterUserTypes?.length) {
      filtered = filtered.filter((u) => filterUserTypes.includes(u.user_type));
    }
    if (filterUserIds?.length) {
      const idSet = new Set(filterUserIds);
      filtered = filtered.filter((u) => idSet.has(u.id));
    }
    if (excludeUserIds?.length) {
      const excluded = new Set(excludeUserIds);
      filtered = filtered.filter((u) => !excluded.has(u.id));
    }
    return filtered.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [usersData, filterUserTypes, filterUserIds, excludeUserIds]);

  const userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  const selectedUser = useMemo(
    () => (value ? userMap.get(value) || null : null),
    [value, userMap]
  );

  const renderUserTypeIndicator = (user: User) => {
    if (!showUserType || user.user_type === 'internal') return null;

    if (user.user_type === 'ai_agent') {
      return (
        <Badge variant="destructive" className="h-4 px-1 text-[10px] gap-0.5">
          <Bot className="h-2.5 w-2.5" />
          {t('userType.ai_agent', 'AI')}
        </Badge>
      );
    }
    if (user.user_type === 'external') {
      return (
        <Badge variant="outline" className="h-4 px-1 text-[10px] gap-0.5">
          <ExternalLink className="h-2.5 w-2.5" />
          {t('userType.external', 'External')}
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <Label className="mb-1.5 block">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || isLoading}
            title={compact && selectedUser?.email ? `${selectedUser.full_name} (${selectedUser.email})` : undefined}
            className={cn(
              'justify-between font-normal',
              fullWidth && 'w-full',
              size === 'sm' && 'h-8 text-sm',
              !selectedUser && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {selectedUser ? (
              <span className="flex items-center gap-2 truncate">
                <Avatar className="h-5 w-5 shrink-0">
                  <AvatarImage src={getAvatarUrl(selectedUser)} alt={selectedUser.full_name} />
                  <AvatarFallback className="text-[10px]">
                    {getInitials(selectedUser.first_name, selectedUser.last_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex flex-col items-start leading-tight min-w-0">
                  <span className="flex items-center gap-1 truncate">
                    <span className="truncate">{selectedUser.full_name}</span>
                    {renderUserTypeIndicator(selectedUser)}
                  </span>
                  {selectedUser.email && !compact && (
                    <span className="text-muted-foreground text-xs truncate max-w-full">
                      {selectedUser.email}
                    </span>
                  )}
                </span>
              </span>
            ) : (
              resolvedPlaceholder
            )}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchUser', 'Search users...')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noUserFound', 'No user found')}</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    value={`${user.full_name} ${user.email}`}
                    onSelect={() => {
                      onChange(user.id === value ? null : user.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        'me-2 h-4 w-4 shrink-0',
                        value === user.id ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    <Avatar className="h-6 w-6 me-2">
                      <AvatarImage src={getAvatarUrl(user)} alt={user.full_name} />
                      <AvatarFallback className="text-xs">
                        {getInitials(user.first_name, user.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <span className="truncate">{user.full_name}</span>
                        {renderUserTypeIndicator(user)}
                      </div>
                      <span className="text-muted-foreground text-xs truncate block">
                        {user.email}
                      </span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

interface MultiUserPickerProps {
  value: string[];
  onChange: (userIds: string[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'sm' | 'default';
  className?: string;
  showUserType?: boolean;
  filterUserTypes?: UserType[];
  maxUsers?: number;
}

export function MultiUserPicker({
  value,
  onChange,
  label,
  placeholder,
  helperText,
  error,
  errorMessage,
  disabled,
  required,
  fullWidth = true,
  size = 'default',
  className,
  showUserType = true,
  filterUserTypes,
  maxUsers,
}: MultiUserPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const resolvedPlaceholder = placeholder ?? t('pickers.selectUsers', 'Select users');

  const { data: usersData, isLoading } = useUsers();

  const users = useMemo(() => {
    if (!usersData?.data) return [];
    let filtered = usersData.data;
    if (filterUserTypes?.length) {
      filtered = filtered.filter((u) => filterUserTypes.includes(u.user_type));
    }
    return filtered.sort((a, b) => a.full_name.localeCompare(b.full_name));
  }, [usersData, filterUserTypes]);

  const userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);

  const selectedUsers = useMemo(
    () => value.map((id) => userMap.get(id)).filter(Boolean) as User[],
    [value, userMap]
  );

  const handleSelect = (userId: string) => {
    if (value.includes(userId)) {
      onChange(value.filter((id) => id !== userId));
    } else if (!maxUsers || value.length < maxUsers) {
      onChange([...value, userId]);
    }
  };

  const handleRemove = (userId: string) => {
    onChange(value.filter((id) => id !== userId));
  };

  const renderUserTypeIndicator = (user: User, small = false) => {
    if (!showUserType || user.user_type === 'internal') return null;

    const sizeClass = small ? 'h-3 px-0.5 text-[8px]' : 'h-4 px-1 text-[10px]';
    const iconSize = small ? 'h-2 w-2' : 'h-2.5 w-2.5';

    if (user.user_type === 'ai_agent') {
      return (
        <Badge variant="destructive" className={cn(sizeClass, 'gap-0.5')}>
          <Bot className={iconSize} />
        </Badge>
      );
    }
    if (user.user_type === 'external') {
      return (
        <Badge variant="outline" className={cn(sizeClass, 'gap-0.5')}>
          <ExternalLink className={iconSize} />
        </Badge>
      );
    }
    return null;
  };

  return (
    <div className={cn(fullWidth && 'w-full', className)}>
      {label && (
        <Label className="mb-1.5 block">
          {label}
          {required && <span className="text-destructive ms-1">*</span>}
        </Label>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled || isLoading}
            className={cn(
              'justify-between font-normal h-auto min-h-9',
              fullWidth && 'w-full',
              size === 'sm' && 'min-h-8 text-sm',
              !selectedUsers.length && 'text-muted-foreground',
              error && 'border-destructive'
            )}
          >
            {selectedUsers.length > 0 ? (
              <div className="flex flex-wrap gap-1 py-0.5">
                {selectedUsers.map((user) => (
                  <Badge
                    key={user.id}
                    variant="outline"
                    className="gap-1 pl-1 pr-0.5 py-0 font-normal bg-muted/50 text-sm"
                  >
                    <Avatar className="h-4 w-4">
                      <AvatarImage src={getAvatarUrl(user)} alt={user.full_name} />
                      <AvatarFallback className="text-[8px]">
                        {getInitials(user.first_name, user.last_name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="truncate max-w-[120px]">{user.full_name}</span>
                    {renderUserTypeIndicator(user, true)}
                    <button
                      type="button"
                      className="ms-0.5 rounded-full p-0.5 hover:bg-muted"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(user.id);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              resolvedPlaceholder
            )}
            <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command>
            <CommandInput placeholder={t('pickers.searchUser', 'Search users...')} />
            <CommandList>
              <CommandEmpty>{t('pickers.noUserFound', 'No user found')}</CommandEmpty>
              <CommandGroup>
                {users.map((user) => {
                  const isSelected = value.includes(user.id);
                  const isDisabled = !isSelected && !!maxUsers && value.length >= maxUsers;

                  return (
                    <CommandItem
                      key={user.id}
                      value={`${user.full_name} ${user.email}`}
                      onSelect={() => !isDisabled && handleSelect(user.id)}
                      disabled={isDisabled}
                      className={cn(isDisabled && 'opacity-50')}
                    >
                      <Check
                        className={cn(
                          'me-2 h-4 w-4 shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <Avatar className="h-6 w-6 me-2">
                        <AvatarImage src={getAvatarUrl(user)} alt={user.full_name} />
                        <AvatarFallback className="text-xs">
                          {getInitials(user.first_name, user.last_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="truncate">{user.full_name}</span>
                          {renderUserTypeIndicator(user)}
                        </div>
                        <span className="text-muted-foreground text-xs truncate block">
                          {user.email}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {(errorMessage || helperText) && (
        <p className={cn(
          'text-xs mt-1',
          error || errorMessage ? 'text-destructive' : 'text-muted-foreground'
        )}>
          {errorMessage || helperText}
        </p>
      )}
    </div>
  );
}

export function getUserById(users: User[], id: string): User | undefined {
  return users.find((u) => u.id === id);
}
